import React, { useState, useEffect } from 'react';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SavingsIcon from '@mui/icons-material/Savings';
import PersonIcon from '@mui/icons-material/Person';
import PaymentForm from '../TrustPayments/PaymentForm';
import useApi from '../Utils/useApi';

const theme = createTheme({
  typography: {
    htmlFontSize: 20,
    h1: {
      fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
      fontWeight: 'bold',
    },
    h5: {
      fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
      fontWeight: 'bold',
    },
    body1: {
      fontSize: '1.1rem',
    },
  },
});

const UpgradeToAnnual = () => {
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState(null);
  const [noOfferReason, setNoOfferReason] = useState(null);
  const [requestReference, setRequestReference] = useState('');
  const [processing, setProcessing] = useState(false);
  const api = useApi();

  useEffect(() => {
    fetchUpgradeOffer();
  }, []);

  const fetchUpgradeOffer = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('Please log in to view your upgrade offer');
        setLoading(false);
        return;
      }

      const response = await api.get('/upgrade-offer/my-offer', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.available) {
        setOffer(response.data.offer);
      } else {
        setNoOfferReason(response.data);
      }
    } catch (err) {
      console.error('Error fetching upgrade offer:', err);
      setError('Unable to load your upgrade offer. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.post('/upgrade-offer/accept', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRequestReference(response.data.request_reference);
    } catch (err) {
      console.error('Error accepting upgrade:', err);
      setError('Unable to process your upgrade. Please try again.');
      setProcessing(false);
    }
  };

  // Calculate monthly cost for comparison
  const getMonthlyEquivalent = () => {
    if (!offer) return 0;
    return (offer.annual_price / 12).toFixed(2);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <CircularProgress />
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="sm">
          <Box sx={{ mt: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (noOfferReason) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="sm">
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Paper elevation={3} sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom>
                {noOfferReason.reason === 'already_annual'
                  ? "You're Already on Annual!"
                  : 'No Upgrade Available'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {noOfferReason.message}
              </Typography>
              <Button
                variant="contained"
                sx={{ mt: 3 }}
                onClick={() => window.location.href = '/ParentDashboard'}
              >
                Go to Dashboard
              </Button>
            </Paper>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="sm">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            {/* Header */}
            <Typography variant="h1" align="center" gutterBottom sx={{ color: '#1976d2' }}>
              Upgrade to Annual
            </Typography>

            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
              Save big by switching to an annual plan
            </Typography>

            {/* Savings highlight */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: '#e8f5e9',
                border: '2px solid #4caf50',
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SavingsIcon sx={{ fontSize: 40, color: '#4caf50', mr: 2 }} />
                <Box>
                  <Typography variant="h5" sx={{ color: '#2e7d32' }}>
                    Save £{offer.annual_savings} per year!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    That's like getting {Math.round(offer.annual_savings / offer.current_monthly_price)} months free
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Plan details */}
            <Typography variant="h5" gutterBottom>
              Your {offer.plan_tier} Plan
            </Typography>

            <List>
              {offer.children.map((child, index) => (
                <ListItem key={child.id}>
                  <ListItemIcon>
                    <PersonIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={child.name}
                    secondary={child.end_date ? `Current subscription ends: ${new Date(child.end_date).toLocaleDateString('en-GB')}` : null}
                  />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Price comparison */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" color="text.secondary">
                  Currently paying (monthly):
                </Typography>
                <Typography variant="body1" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                  £{offer.current_monthly_price}/month
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" color="text.secondary">
                  That's per year:
                </Typography>
                <Typography variant="body1" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                  £{offer.current_monthly_price * 12}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" color="primary">
                  Annual price:
                </Typography>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                  £{offer.annual_price}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" align="right">
                Just £{getMonthlyEquivalent()}/month
              </Typography>
            </Box>

            {/* Benefits */}
            <Box sx={{ mb: 3 }}>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary="No price increases for 12 months" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Remaining days from your monthly plan added on" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Cancel anytime with no penalties" />
                </ListItem>
              </List>
            </Box>

            {/* Upgrade button */}
            {!requestReference && (
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleUpgrade}
                disabled={processing}
                sx={{
                  py: 2,
                  fontSize: '1.2rem',
                  backgroundColor: '#4caf50',
                  '&:hover': {
                    backgroundColor: '#388e3c'
                  }
                }}
              >
                {processing ? <CircularProgress size={24} color="inherit" /> : `Upgrade Now - £${offer.annual_price}`}
              </Button>
            )}

            {/* Payment Form - renders hidden and auto-submits */}
            {requestReference && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Redirecting to payment...
                </Typography>
                <PaymentForm
                  domain="https://payments.securetrading.net"
                  amount={offer.annual_price}
                  currency="GBP"
                  requestReference={requestReference}
                  autoRenew={true}
                />
              </Box>
            )}

            {/* Note about remaining days */}
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
              Any remaining days on your current monthly subscription will be added to your new annual plan.
            </Typography>
          </Paper>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default UpgradeToAnnual;
